import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Settings, UserCircle, X } from "lucide-react";
import { motion } from "framer-motion";
import { listChildren, createChild, switchChild, deleteChild } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BoardPicker from "@/components/BoardPicker";

const AVATAR_COLORS = ["#D4C4FB", "#FFE066", "#9DFFB0", "#FFB38E", "#90E0FF"];

function Avatar({ seed, size = 96 }) {
  const url = `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(seed)}`;
  const color = AVATAR_COLORS[(seed?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div
      className="nb-border rounded-2xl overflow-hidden"
      style={{ background: color, width: size, height: size }}
    >
      <img src={url} alt={seed} className="w-full h-full" />
    </div>
  );
}

function NewChildDialog({ open, onClose, onCreated, parentMax, currentCount }) {
  const [name, setName] = useState("");
  const [year, setYear] = useState(10);
  const [board, setBoard] = useState("AQA GCSE");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;
  const canAdd = currentCount < parentMax;

  const submit = async (e) => {
    e.preventDefault();
    if (!canAdd) {
      toast.error(`Your plan allows up to ${parentMax} child profile(s).`);
      return;
    }
    if (pin !== pin2 || pin.length !== 4) {
      toast.error("PINs must match and be 4 digits.");
      return;
    }
    setSaving(true);
    try {
      const c = await createChild({
        name: name.trim(),
        year_group: year,
        pin,
        avatar_seed: name.trim(),
        exam_board: board,
      });
      onCreated(c);
      onClose();
      toast.success("Profile created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="nb-card p-6 w-full max-w-md nb-shadow-xl bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">Add child profile</h2>
          <button type="button" onClick={onClose} className="nb-button p-2"><X className="w-4 h-4" /></button>
        </div>
        {!canAdd && (
          <div className="nb-border rounded-xl p-3 bg-[#FFB38E] mb-4 text-sm font-bold text-black">
            Your current plan allows up to {parentMax} profile(s). Upgrade for more siblings.
          </div>
        )}
        <label className="label-caps block mb-2">Child&apos;s name</label>
        <input
          data-testid="new-child-name"
          autoFocus
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Lily"
          className="nb-input mb-4"
        />
        <label className="label-caps block mb-2">Year group</label>
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          data-testid="new-child-year"
          className="nb-input mb-4"
        >
          {[7,8,9,10,11,12,13].map((y) => <option key={y} value={y}>Year {y}</option>)}
        </select>
        <label className="label-caps block mb-2">Exam board</label>
        <div className="mb-4">
          <BoardPicker value={board} onChange={setBoard} testIdPrefix="new-child-board" />
        </div>
        <label className="label-caps block mb-2">4-digit PIN (kid login)</label>
        <div className="flex gap-2 mb-4">
          <input
            data-testid="new-child-pin"
            required maxLength={4} pattern="\d{4}" inputMode="numeric"
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g,""))}
            placeholder="e.g. 1234" className="nb-input"
          />
          <input
            data-testid="new-child-pin2"
            required maxLength={4} pattern="\d{4}" inputMode="numeric"
            value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g,""))}
            placeholder="confirm" className="nb-input"
          />
        </div>
        <button
          type="submit"
          disabled={saving || !canAdd}
          data-testid="create-child-submit"
          className="nb-button w-full py-3 bg-[#D4C4FB] dark:bg-[#B794F4]"
        >
          {saving ? "Creating…" : "Create profile"}
        </button>
      </form>
    </div>
  );
}

export default function Family() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [pinFor, setPinFor] = useState(null);
  const [pin, setPin] = useState("");
  const [manage, setManage] = useState(false);
  const [showFamilyUpsell, setShowFamilyUpsell] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setChildren(await listChildren()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tryEnter = async (child) => {
    if (children.length === 1) {
      // no PIN needed for solo
      await doSwitch(child, null);
      return;
    }
    setPinFor(child);
    setPin("");
  };

  const doSwitch = async (child, pinValue) => {
    try {
      await switchChild(child.id, pinValue);
      await refresh();
      navigate("/curriculum");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn&apos;t enter profile");
    }
  };

  const removeChild = async (id) => {
    if (!window.confirm("Delete this child profile?")) return;
    await deleteChild(id);
    await refresh();
    load();
  };

  const familyName = (user?.family_name || "").trim();
  const showFamilyHeading = familyName && familyName.toLowerCase() !== "family";

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 p-6 sm:p-10">
      <main className="max-w-[960px] mx-auto">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            {showFamilyHeading ? (
              <>
                <div className="label-caps mb-2">Who&apos;s studying?</div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                  The {familyName} family
                </h1>
              </>
            ) : (
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                Who&apos;s studying?
              </h1>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="family-leaderboard-link"
              onClick={() => navigate("/leaderboard")}
              className="nb-button px-3 py-2 flex items-center gap-1.5"
            >
              <span className="text-sm font-bold">Leaderboard</span>
            </button>
            <button
              data-testid="family-manage-toggle"
              onClick={() => setManage((v) => !v)}
              aria-label={manage ? "Done editing profiles" : "Edit profiles"}
              className={`nb-button px-3 py-2 flex items-center gap-1.5 ${manage ? "bg-[#FFE066] dark:bg-[#F6E05E]" : ""}`}
            >
              <Settings className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-bold">{manage ? "Done" : "Settings"}</span>
            </button>
            <button
              data-testid="parent-dashboard-link"
              onClick={() => navigate("/parent")}
              className="nb-button px-4 py-2 text-sm"
            >
              Parent dashboard
            </button>
          </div>
        </div>

        {loading ? (
          <div className="font-bold text-zinc-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
            {children.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="relative"
              >
                <button
                  data-testid={`child-tile-${c.id}`}
                  onClick={() => tryEnter(c)}
                  className="nb-card p-5 w-full flex flex-col items-center gap-3 group"
                >
                  <Avatar seed={c.avatar_seed || c.name} />
                  <div>
                    <div className="font-display text-xl font-extrabold">{c.name}</div>
                    <div className="text-xs font-bold text-zinc-500">Year {c.year_group}</div>
                    {c.exam_board && (
                      <div
                        data-testid={`child-board-${c.id}`}
                        className="mt-1.5 inline-block nb-border rounded-full px-2 py-0.5 text-[10px] font-extrabold bg-white dark:bg-zinc-900"
                      >
                        {c.exam_board.replace(" GCSE", "")}
                      </div>
                    )}
                  </div>
                </button>
                {manage && (
                  <button
                    onClick={() => removeChild(c.id)}
                    data-testid={`child-delete-${c.id}`}
                    className="absolute -top-2 -right-2 nb-border rounded-full bg-[#FFB38E] p-2 nb-shadow"
                  >
                    <X className="w-4 h-4 text-black" />
                  </button>
                )}
              </motion.div>
            ))}
            {children.length < (user?.max_children || 1) && (
              <button
                data-testid="add-child-tile"
                id="add-child-btn"
                onClick={() => setShowNew(true)}
                className="nb-border rounded-2xl p-5 flex flex-col items-center justify-center gap-3 border-dashed bg-white dark:bg-zinc-900 hover:nb-shadow transition-all min-h-[180px]"
              >
                <div className="w-20 h-20 nb-border rounded-2xl flex items-center justify-center bg-zinc-50 dark:bg-zinc-800 border-dashed">
                  <Plus className="w-10 h-10" strokeWidth={3} aria-hidden="true" />
                </div>
                <div className="font-display text-base font-extrabold">Add a child</div>
              </button>
            )}
            {children.length >= (user?.max_children || 1) && children.length < 3 && (
              <button
                data-testid="family-upsell-tile"
                onClick={() => setShowFamilyUpsell(true)}
                className="nb-border rounded-2xl p-5 flex flex-col items-center justify-center gap-2 border-dashed bg-[#FFE066]/40 dark:bg-[#F6E05E]/20 hover:nb-shadow transition-all min-h-[180px]"
              >
                <div className="w-20 h-20 nb-border rounded-2xl flex items-center justify-center bg-white dark:bg-zinc-900 border-dashed">
                  <Plus className="w-10 h-10" strokeWidth={3} aria-hidden="true" />
                </div>
                <div className="font-display text-base font-extrabold text-center leading-tight">Add a child</div>
                <div className="text-xs font-bold text-muted">Family plan</div>
              </button>
            )}
          </div>
        )}

        {children.length === 0 && !loading && (
          <div className="nb-card p-8 mt-10 text-center">
            <UserCircle className="w-12 h-12 mx-auto mb-3" strokeWidth={2} />
            <div className="font-display text-2xl font-extrabold mb-2">Create your first profile</div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-4">
              Each child gets a separate profile with their own progress and streak.
            </p>
            <button onClick={() => setShowNew(true)} className="nb-button px-6 py-3 bg-[#D4C4FB] dark:bg-[#B794F4]">
              Add child
            </button>
          </div>
        )}

        <NewChildDialog
          open={showNew}
          onClose={() => setShowNew(false)}
          onCreated={async () => { await refresh(); load(); }}
          parentMax={user?.max_children || 1}
          currentCount={children.length}
        />

        {/* PIN modal */}
        {pinFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPinFor(null)}>
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => { e.preventDefault(); doSwitch(pinFor, pin); }}
              className="nb-card p-6 w-full max-w-sm nb-shadow-xl bg-white dark:bg-zinc-900 text-center"
            >
              <Avatar seed={pinFor.avatar_seed || pinFor.name} size={80} />
              <div className="font-display text-2xl font-extrabold mt-4 mb-2">{pinFor.name}</div>
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-4">Enter your 4-digit PIN</p>
              <input
                autoFocus inputMode="numeric" maxLength={4} pattern="\d{4}"
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g,""))}
                data-testid="child-pin-input"
                className="nb-input text-center text-2xl tracking-[0.5em] mb-4"
              />
              <button type="submit" data-testid="child-pin-submit" className="nb-button w-full py-3 bg-[#9DFFB0] dark:bg-[#68D391]">
                Enter
              </button>
            </form>
          </div>
        )}
      </main>
      {showFamilyUpsell && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowFamilyUpsell(false)}
          data-testid="family-upsell-modal"
        >
          <div className="nb-card bg-white dark:bg-zinc-900 w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl font-black">Add another child</h2>
            <p className="text-sm font-medium text-muted">
              Family plan lets you add up to 3 children for £7.99/month. Each child gets their own profile, progress and streak.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowFamilyUpsell(false)} className="nb-button px-4 py-2 flex-1 text-sm">Not now</button>
              <button
                onClick={() => { setShowFamilyUpsell(false); navigate("/pricing"); }}
                className="nb-button-primary px-4 py-2 flex-1 text-sm"
                data-testid="family-upsell-see-plan"
              >
                See Family plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
